/**
 * URL query 字段解析与规范化
 *
 * 职责：
 * - ac_id / c_id 的拆分、去重、校验
 * - 账户权限判定（accountList 三态：未加载 null / 已加载空 [] / 有数据）
 * - 从 query 中安全读取常用字段
 */
import { QUERY_KEYS } from './constants';

/**
 * 通用 ID 列表解析：逗号分隔 → 去重数组
 * ac_id / c_id 共用，避免两份几乎相同的函数
 * @param {string} idStr 逗号分隔的 ID 字符串（如 "123,456,123"）
 * @returns {string[]} 去重后的 ID 数组（如 ["123","456"]）
 */
function splitAndDedup(idStr = '') {
    if (!idStr) return [];
    return [...new Set(String(idStr).split(',').map(id => id.trim()).filter(Boolean))];
}

/**
 * 解析 c_id 为 campaign ID 数组
 * @example resolveCampaignIds('100,200,100') → ['100','200']
 */
export function resolveCampaignIds(campaignIdStr = '') {
    return splitAndDedup(campaignIdStr);
}

/** c_id 是否表示多 campaign 选择（ID 数 > 1） */
export function isMultiCampaignSelection(campaignIdStr = '') {
    return resolveCampaignIds(campaignIdStr).length > 1;
}

/**
 * 解析 ac_id 为 account ID 数组
 * @example resolveAccountIds('890,891') → ['890','891']
 */
export function resolveAccountIds(accountIdStr = '') {
    return splitAndDedup(accountIdStr);
}

/**
 * 校验账户是否在 accountList 中有权限
 *
 * 三态返回值对应 accountList 的三种状态：
 * - null  → accountList 尚未从接口加载（初始值 null），无法判定
 * - false → accountList 已加载但为空 / 指定账户不在列表中
 * - true  → 所有指定账户均在列表中
 *
 * @param {Array|null} accountList 账户下拉列表（null = 未加载）
 * @param {string} accountIdStr 逗号分隔的账户 ID
 * @returns {true|false|null}
 */
export function hasAccountAccess(accountList, accountIdStr) {
    const ids = resolveAccountIds(accountIdStr);
    if (!ids.length) return false;
    if (!Array.isArray(accountList)) return null;   // 未加载
    if (!accountList.length) return false;           // 已加载但无权限
    return ids.every(id => accountList.some(option => String(option.value) === id));
}

/**
 * 写入 URL 时规范化 ac_id（去重 + 逗号拼接）
 * @example normalizeAccountIdForQuery('890, 890,891') → '890,891'
 */
export function normalizeAccountIdForQuery(accountIdStr = '') {
    return resolveAccountIds(accountIdStr).join(',');
}

/** 从 query 读取 ac_id（缺省返回空串） */
export function getQueryAccountId(query = {}) {
    return query[QUERY_KEYS.ACCOUNT_ID] || '';
}

/** 从 query 读取 c_id（缺省返回空串） */
export function getQueryCampaignId(query = {}) {
    return query[QUERY_KEYS.CAMPAIGN_ID] || '';
}

/**
 * 从 query 读取时间粒度 latitude
 * @returns {'1'|'2'|''} '1' = 天，'2' = 小时，非法值返回空串
 */
export function getQueryLatitude(query = {}) {
    const value = query[QUERY_KEYS.LATITUDE] || '';
    return value === '1' || value === '2' ? value : '';
}
